import PusherJS from "pusher-js";

import Api from "js/core/api";
import { isDevelopment, pusherKey, pusherCluster } from "js/config";
import log from "js/core/logger";

if (isDevelopment) {
    PusherJS.logToConsole = false;
}

let pusherClient;
function getPusherClient() {
    if (window.disablePusher) {
        return null;
    }

    if (!pusherClient) {
        log.info(log.PUSHER)("getPusherClient() instantiating pusher client");
        pusherClient = new PusherJS(pusherKey, {
            cluster: pusherCluster,
            authorizer: channel => ({
                authorize: (socketId, callback) => {
                    const channelId = channel.name;

                    log.info(log.PUSHER)(`authorize() channelId: ${channelId}`);
                    Api.pusherAuthenticate.post({ channelId, socketId })
                        .then(authData => callback(null, authData))
                        .catch(err => callback(err, { auth: "" }));
                }
            })
        });
        pusherClient.connection.bind("error", err => {
            if (err.type === "PusherError" && (
                (err.data.code >= 4200 && err.data.code <= 4299) ||
                err.data.code === 1006
            )) {
                // Non critical errors upon which the connection will be re-established
                log.warn(`[pusher] connnection error, error: ${JSON.stringify(err, null, 2)}`);
            } else {
                log.error(`[pusher] critical connnection error, error: ${JSON.stringify(err, null, 2)}`);
            }
        });
    }

    if (isDevelopment) {
        // Debug
        window.pusherClient = pusherClient;
    }

    return pusherClient;
}

function ensureSubscribed(channel) {
    log.info(log.PUSHER)(`ensureSubscribed() channelId: ${channel.name}`);

    return new Promise((resolve, reject) => {
        const onError = err => {
            channel.unbind("pusher:subscription_error", onError);
            channel.unbind("pusher:subscription_succeeded", onSuccess);
            reject(err);
        };
        const onSuccess = () => {
            channel.unbind("pusher:subscription_error", onError);
            channel.unbind("pusher:subscription_succeeded", onSuccess);
            resolve();
        };
        channel.bind("pusher:subscription_error", onError);
        channel.bind("pusher:subscription_succeeded", onSuccess);
    });
}

/**
 * Subscribes to a channel
 */
async function subscribe(channelId) {
    log.info(log.PUSHER)(`subscribe() channelId: ${channelId}`);

    const pusherClient = getPusherClient();
    if (!pusherClient) {
        log.info(log.PUSHER)("subscribe() pusher is disabled, ignoring...");
        return null;
    }

    const existingChannel = pusherClient.channels.find(channelId);
    if (existingChannel) {
        if (existingChannel.subscribed) {
            log.info(log.PUSHER)(`subscribe() already subscribed to channel ${channelId}`);
            return existingChannel;
        }

        if (existingChannel.subscriptionPending) {
            await ensureSubscribed(existingChannel);
            return existingChannel;
        }
    }

    // Subscribing to channel and waiting for subscription to establish
    const channel = pusherClient.subscribe(channelId);
    await ensureSubscribed(channel);
    return channel;
}

/**
 * Waits for a specific event
 */
async function waitForEvent(channelId, successEventType, errorEventType, timeoutMs) {
    const channel = await subscribe(channelId);

    let onFinished, onError;
    return new Promise((resolve, reject) => {
        const timeoutHandler = setTimeout(() => {
            channel.unsubscribe();
            reject(new Error(`Failed to receive event for channel id ${channelId}, timed out after ${timeoutMs}ms`));
        }, timeoutMs);

        onFinished = response => {
            channel.unsubscribe();
            clearTimeout(timeoutHandler);
            resolve(response);
        };
        onError = response => {
            channel.unsubscribe();
            clearTimeout(timeoutHandler);
            reject(new Error(response.errorMessage));
        };

        channel.bind(successEventType, onFinished);
        channel.bind(errorEventType, onError);
    });
}

module.exports = { subscribe, waitForEvent };

